import type { Driver, Rider, Trip, Vehicle, Complaint, City, VehicleCategory, DashboardStats, FinanceData, Settings, Notification, User } from '@shared/types';
import type { Coupon, CouponUsage, CouponStats, CouponAnalytics } from '../../../shared/types/coupon';

// Current user for authentication
export const currentUser: User = {
  id: 1,
  email: "admin@example.com",
  name: "Admin User",
  role: "super_admin",
  isActive: true,
  createdAt: new Date("2024-01-01").toISOString(),
};

// Dashboard statistics
export const dashboardStats: DashboardStats = {
  activeDrivers: 847,
  ongoingRides: 234,
  dailyTrips: 5847,
  revenueToday: 125847.50,
  totalDrivers: 1247,
  totalRiders: 45678,
  totalVehicles: 892,
  pendingComplaints: 23,
  monthlyRevenue: 2847562.30,
  monthlyTrips: 156789,
  averageRating: 4.6,
  completionRate: 94.2,
};

// Drivers data
export const drivers: Driver[] = [
  {
    id: 1,
    name: "John Smith",
    email: "john.smith@example.com",
    phone: "+1234567890",
    licenseNumber: "DL123456789",
    licenseExpiry: new Date("2025-12-31"),
    status: "active",
    isOnline: true,
    rating: "4.8",
    totalTrips: 1247,
    city: "New York",
    vehicleId: 1,
    createdAt: new Date("2023-01-15"),
  },
  {
    id: 2,
    name: "Sarah Johnson",
    email: "sarah.johnson@example.com",
    phone: "+1234567891",
    licenseNumber: "DL987654321",
    licenseExpiry: new Date("2025-06-15"),
    status: "active",
    isOnline: false,
    rating: "4.6",
    totalTrips: 892,
    city: "Los Angeles",
    vehicleId: 2,
    createdAt: new Date("2023-03-20"),
  },
  {
    id: 3,
    name: "Michael Brown",
    email: "michael.brown@example.com",
    phone: "+1234567892",
    licenseNumber: "DL456789123",
    licenseExpiry: new Date("2024-11-30"),
    status: "suspended",
    isOnline: false,
    rating: "4.2",
    totalTrips: 567,
    city: "Chicago",
    vehicleId: null,
    createdAt: new Date("2023-05-10"),
  },
  {
    id: 4,
    name: "Emily Davis",
    email: "emily.davis@example.com",
    phone: "+1234567893",
    licenseNumber: "DL789123456",
    licenseExpiry: new Date("2026-02-28"),
    status: "active",
    isOnline: true,
    rating: "4.9",
    totalTrips: 1567,
    city: "Miami",
    vehicleId: 3,
    createdAt: new Date("2023-02-08"),
  },
  {
    id: 5,
    name: "David Wilson",
    email: "david.wilson@example.com",
    phone: "+1234567894",
    licenseNumber: "DL321654987",
    licenseExpiry: new Date("2025-08-15"),
    status: "inactive",
    isOnline: false,
    rating: "4.4",
    totalTrips: 734,
    city: "Seattle",
    vehicleId: null,
    createdAt: new Date("2023-04-12"),
  },
];

// Riders data
export const riders: Rider[] = [
  {
    id: 1,
    name: "Alice Cooper",
    email: "alice.cooper@example.com",
    phone: "+1987654321",
    status: "active",
    totalTrips: 89,
    createdAt: new Date("2023-01-10"),
  },
  {
    id: 2,
    name: "Bob Martinez",
    email: "bob.martinez@example.com",
    phone: "+1987654322",
    status: "active",
    totalTrips: 156,
    createdAt: new Date("2023-02-15"),
  },
  {
    id: 3,
    name: "Carol White",
    email: "carol.white@example.com",
    phone: "+1987654323",
    status: "banned",
    totalTrips: 23,
    createdAt: new Date("2023-03-20"),
  },
  {
    id: 4,
    name: "Daniel Lee",
    email: "daniel.lee@example.com",
    phone: "+1987654324",
    status: "active",
    totalTrips: 234,
    createdAt: new Date("2023-01-25"),
  },
  {
    id: 5,
    name: "Eva Rodriguez",
    email: "eva.rodriguez@example.com",
    phone: "+1987654325",
    status: "restricted",
    totalTrips: 67,
    createdAt: new Date("2023-04-05"),
  },
];

// Vehicle categories
export const vehicleCategories: VehicleCategory[] = [
  {
    id: 1,
    name: "Economy",
    baseFare: "5.00",
    perKmRate: "1.50",
    perMinuteRate: "0.25",
    isActive: true,
  },
  {
    id: 2,
    name: "Comfort",
    baseFare: "7.00",
    perKmRate: "2.00",
    perMinuteRate: "0.30",
    isActive: true,
  },
  {
    id: 3,
    name: "Premium",
    baseFare: "10.00",
    perKmRate: "3.00",
    perMinuteRate: "0.45",
    isActive: true,
  },
  {
    id: 4,
    name: "XL",
    baseFare: "8.00",
    perKmRate: "2.50",
    perMinuteRate: "0.35",
    isActive: true,
  },
];

// Vehicles data
export const vehicles: Vehicle[] = [
  {
    id: 1,
    make: "Toyota",
    model: "Camry",
    year: 2022,
    licensePlate: "ABC123",
    color: "Silver",
    categoryId: 2,
    driverId: 1,
    status: "active",
    createdAt: new Date("2023-01-15"),
  },
  {
    id: 2,
    make: "Honda",
    model: "Civic",
    year: 2021,
    licensePlate: "XYZ789",
    color: "Blue",
    categoryId: 1,
    driverId: 2,
    status: "active",
    createdAt: new Date("2023-03-20"),
  },
  {
    id: 3,
    make: "BMW",
    model: "3 Series",
    year: 2023,
    licensePlate: "DEF456",
    color: "Black",
    categoryId: 3,
    driverId: 4,
    status: "active",
    createdAt: new Date("2023-02-08"),
  },
  {
    id: 4,
    make: "Ford",
    model: "Escape",
    year: 2022,
    licensePlate: "GHI789",
    color: "White",
    categoryId: 4,
    driverId: null,
    status: "maintenance",
    createdAt: new Date("2023-04-12"),
  },
  {
    id: 5,
    make: "Nissan",
    model: "Altima",
    year: 2021,
    licensePlate: "JKL012",
    color: "Red",
    categoryId: 1,
    driverId: null,
    status: "retired",
    createdAt: new Date("2023-01-30"),
  },
];

// Trips data
export const trips: Trip[] = [
  {
    id: 1,
    tripId: "TRIP001",
    driverId: 1,
    riderId: 1,
    vehicleId: 1,
    pickupLocation: "123 Main St, New York",
    dropoffLocation: "456 Park Ave, New York",
    distance: "5.2",
    duration: 18,
    fare: "12.50",
    commission: "2.50",
    status: "completed",
    createdAt: new Date("2024-01-15T10:30:00"),
    completedAt: new Date("2024-01-15T10:48:00"),
  },
  {
    id: 2,
    tripId: "TRIP002",
    driverId: 2,
    riderId: 2,
    vehicleId: 2,
    pickupLocation: "789 Sunset Blvd, Los Angeles",
    dropoffLocation: "321 Hollywood Blvd, Los Angeles",
    distance: "8.7",
    duration: 25,
    fare: "18.75",
    commission: "3.75",
    status: "ongoing",
    createdAt: new Date("2024-01-15T11:15:00"),
    completedAt: null,
  },
  {
    id: 3,
    tripId: "TRIP003",
    driverId: 4,
    riderId: 4,
    vehicleId: 3,
    pickupLocation: "555 Ocean Dr, Miami",
    dropoffLocation: "888 Collins Ave, Miami",
    distance: "3.1",
    duration: 12,
    fare: "15.00",
    commission: "3.00",
    status: "completed",
    createdAt: new Date("2024-01-15T09:45:00"),
    completedAt: new Date("2024-01-15T09:57:00"),
  },
  {
    id: 4,
    tripId: "TRIP004",
    driverId: 1,
    riderId: 1,
    vehicleId: 1,
    pickupLocation: "456 Park Ave, New York",
    dropoffLocation: "789 Broadway, New York",
    distance: "2.8",
    duration: 10,
    fare: "8.75",
    commission: "1.75",
    status: "cancelled",
    createdAt: new Date("2024-01-15T12:00:00"),
    completedAt: null,
  },
  {
    id: 5,
    tripId: "TRIP005",
    driverId: 2,
    riderId: 2,
    vehicleId: 2,
    pickupLocation: "321 Hollywood Blvd, Los Angeles",
    dropoffLocation: "654 Vine St, Los Angeles",
    distance: "4.3",
    duration: 15,
    fare: "11.25",
    commission: "2.25",
    status: "completed",
    createdAt: new Date("2024-01-15T08:30:00"),
    completedAt: new Date("2024-01-15T08:45:00"),
  },
];

// Complaints data
export const complaints: Complaint[] = [
  {
    id: 1,
    tripId: 1,
    reporterId: 1,
    reporterType: "rider",
    category: "driver_issue",
    subject: "Driver was rude",
    description: "The driver was very rude and unprofessional during the trip.",
    status: "open",
    assignedTo: 1,
    resolution: null,
    createdAt: new Date("2024-01-15T11:00:00"),
    resolvedAt: null,
  },
  {
    id: 2,
    tripId: 2,
    reporterId: 2,
    reporterType: "driver",
    category: "payment_issue",
    subject: "Payment failed",
    description: "The rider's payment method failed and they refused to pay cash.",
    status: "in_review",
    assignedTo: 1,
    resolution: "Contacting rider for alternative payment",
    createdAt: new Date("2024-01-15T10:30:00"),
    resolvedAt: null,
  },
  {
    id: 3,
    tripId: 3,
    reporterId: 4,
    reporterType: "rider",
    category: "vehicle_issue",
    subject: "Dirty vehicle",
    description: "The vehicle was not clean and had a bad smell.",
    status: "resolved",
    assignedTo: 1,
    resolution: "Driver has been warned and vehicle cleaned",
    createdAt: new Date("2024-01-14T16:45:00"),
    resolvedAt: new Date("2024-01-15T09:00:00"),
  },
  {
    id: 4,
    tripId: 4,
    reporterId: 1,
    reporterType: "driver",
    category: "route_issue",
    subject: "Rider cancelled without reason",
    description: "Rider cancelled the trip after I arrived at pickup location.",
    status: "open",
    assignedTo: null,
    resolution: null,
    createdAt: new Date("2024-01-15T12:15:00"),
    resolvedAt: null,
  },
  {
    id: 5,
    tripId: 5,
    reporterId: 2,
    reporterType: "rider",
    category: "driver_issue",
    subject: "Driver took longer route",
    description: "Driver took a much longer route than necessary, increasing the fare.",
    status: "in_review",
    assignedTo: 1,
    resolution: "Investigating route taken",
    createdAt: new Date("2024-01-15T09:00:00"),
    resolvedAt: null,
  },
];

// Cities data
export const cities: City[] = [
  {
    id: 1,
    name: "New York",
    country: "USA",
    isActive: true,
    baseFareMultiplier: "1.20",
    maxDrivers: 2000,
  },
  {
    id: 2,
    name: "Los Angeles",
    country: "USA",
    isActive: true,
    baseFareMultiplier: "1.15",
    maxDrivers: 1800,
  },
  {
    id: 3,
    name: "Chicago",
    country: "USA",
    isActive: true,
    baseFareMultiplier: "1.10",
    maxDrivers: 1500,
  },
  {
    id: 4,
    name: "Miami",
    country: "USA",
    isActive: true,
    baseFareMultiplier: "1.25",
    maxDrivers: 1200,
  },
  {
    id: 5,
    name: "Seattle",
    country: "USA",
    isActive: true,
    baseFareMultiplier: "1.05",
    maxDrivers: 1000,
  },
  {
    id: 6,
    name: "San Francisco",
    country: "USA",
    isActive: false,
    baseFareMultiplier: "1.30",
    maxDrivers: 800,
  },
];

// Finance data
export const finance: FinanceData = {
  revenue: {
    today: 125847.50,
    week: 876543.20,
    month: 2847562.30,
    year: 32456789.10,
  },
  expenses: {
    today: 45678.90,
    week: 321456.70,
    month: 1234567.80,
    year: 14567890.20,
  },
  driverPayouts: {
    today: 34567.80,
    week: 234567.90,
    month: 987654.30,
    year: 11876543.20,
  },
  commission: {
    today: 25169.50,
    week: 175308.64,
    month: 569512.46,
    year: 6491357.82,
  },
  transactions: [
    {
      id: 1,
      type: "revenue",
      amount: 125847.50,
      description: "Today's revenue",
      date: new Date("2024-01-15"),
    },
    {
      id: 2,
      type: "expense",
      amount: 45678.90,
      description: "Today's expenses",
      date: new Date("2024-01-15"),
    },
    {
      id: 3,
      type: "payout",
      amount: 34567.80,
      description: "Driver payouts",
      date: new Date("2024-01-15"),
    },
  ],
};

// Notifications data
export const notifications: Notification[] = [
  {
    id: "1",
    title: "System Maintenance",
    message: "Scheduled maintenance will occur tonight from 2-4 AM EST",
    type: "maintenance",
    targetType: "all",
    targetIds: [],
    cityIds: [],
    scheduledFor: new Date("2024-01-16T02:00:00"),
    status: "scheduled",
    sentAt: null,
    createdBy: 1,
    createdAt: new Date("2024-01-15T10:00:00"),
  },
  {
    id: "2",
    title: "New Feature Available",
    message: "Driver earnings dashboard is now available in the app",
    type: "announcement",
    targetType: "drivers",
    targetIds: [],
    cityIds: [],
    scheduledFor: null,
    status: "sent",
    sentAt: new Date("2024-01-15T09:00:00"),
    createdBy: 1,
    createdAt: new Date("2024-01-15T08:30:00"),
  },
  {
    id: "3",
    title: "Promo Code: SAVE20",
    message: "Use code SAVE20 for 20% off your next ride",
    type: "promo",
    targetType: "riders",
    targetIds: [],
    cityIds: [1, 2, 3],
    scheduledFor: null,
    status: "sent",
    sentAt: new Date("2024-01-15T07:00:00"),
    createdBy: 1,
    createdAt: new Date("2024-01-14T16:00:00"),
  },
  {
    id: "4",
    title: "Weather Alert",
    message: "Heavy rain expected in Miami area. Drive safely!",
    type: "alert",
    targetType: "city",
    targetIds: [],
    cityIds: [4],
    scheduledFor: null,
    status: "sent",
    sentAt: new Date("2024-01-15T06:30:00"),
    createdBy: 1,
    createdAt: new Date("2024-01-15T06:00:00"),
  },
];

// Settings data
export const settings: Settings = {
  general: {
    companyName: "RideShare Admin",
    supportEmail: "support@rideshare.com",
    supportPhone: "+1-800-RIDE-HELP",
    timezone: "America/New_York",
    currency: "USD",
    language: "en",
  },
  features: {
    driverApplications: true,
    realTimeTracking: true,
    paymentProcessing: true,
    notifications: true,
    analytics: true,
    reporting: true,
  },
  security: {
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    requireTwoFactor: false,
    passwordPolicy: "strong",
  },
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    notificationFrequency: "immediate",
  },
};

// Mock booking data
export const bookings = [
  {
    id: 501,
    customer: {
      id: 101,
      name: "Ahmed Customer",
      email: "ahmed@example.com",
      phone: "+966501234567",
      language: "ar" as const,
      past_bookings: 5,
      average_rating: 4.7
    },
    property_id: 11,
    property_name: "Deluxe Apartment - Riyadh",
    status: "confirmed" as const,
    check_in: "2025-09-15T15:00:00.000Z",
    check_out: "2025-09-17T12:00:00.000Z",
    guest_count: 2,
    special_requests: "Sea view room",
    payment_summary: {
      total_amount: 1500,
      deposit_paid: 450,
      remaining_amount: 1050,
      payment_method: "mada" as const
    },
    coupon_code: "SAVE20",
    created_at: "2025-01-15T10:00:00.000Z",
    updated_at: "2025-01-15T10:00:00.000Z"
  },
  {
    id: 502,
    customer: {
      id: 102,
      name: "Sara Client",
      email: "sara@example.com",
      phone: "+966501234568",
      language: "en" as const,
      past_bookings: 3,
      average_rating: 4.5
    },
    property_id: 12,
    property_name: "Standard Apartment - Jeddah",
    status: "canceled" as const,
    check_in: "2025-09-20T15:00:00.000Z",
    check_out: "2025-09-22T12:00:00.000Z",
    guest_count: 1,
    special_requests: "Late check-in please",
    payment_summary: {
      total_amount: 800,
      deposit_paid: 240,
      remaining_amount: 560,
      payment_method: "visa" as const
    },
    created_at: "2025-01-14T14:00:00.000Z",
    updated_at: "2025-01-15T09:00:00.000Z"
  },
  {
    id: 503,
    customer: {
      id: 103,
      name: "محمد السعيد",
      email: "mohammed@example.com",
      phone: "+966501234569",
      language: "ar" as const,
      past_bookings: 8,
      average_rating: 4.9
    },
    property_id: 13,
    property_name: "Family Suite - Dammam",
    status: "completed" as const,
    check_in: "2025-01-10T15:00:00.000Z",
    check_out: "2025-01-13T12:00:00.000Z",
    guest_count: 4,
    special_requests: "Family room with extra bed",
    payment_summary: {
      total_amount: 2200,
      deposit_paid: 660,
      remaining_amount: 1540,
      payment_method: "bank_transfer" as const
    },
    created_at: "2025-01-08T09:00:00.000Z",
    updated_at: "2025-01-13T12:00:00.000Z"
  },
  {
    id: 504,
    customer: {
      id: 104,
      name: "Emily Johnson",
      email: "emily@example.com",
      phone: "+966501234570",
      language: "en" as const,
      past_bookings: 2,
      average_rating: 4.3
    },
    property_id: 14,
    property_name: "Luxury Villa - Al Khobar",
    status: "pending" as const,
    check_in: "2025-02-01T15:00:00.000Z",
    check_out: "2025-02-05T12:00:00.000Z",
    guest_count: 2,
    special_requests: "Honeymoon suite",
    payment_summary: {
      total_amount: 3500,
      deposit_paid: 0,
      remaining_amount: 3500,
      payment_method: "visa" as const
    },
    created_at: "2025-01-20T16:00:00.000Z",
    updated_at: "2025-01-20T16:00:00.000Z"
  },
  {
    id: 505,
    customer: {
      id: 105,
      name: "عبدالله الأحمد",
      email: "abdullah@example.com",
      phone: "+966501234571",
      language: "ar" as const,
      past_bookings: 12,
      average_rating: 4.8
    },
    property_id: 15,
    property_name: "Business Suite - Riyadh",
    status: "confirmed" as const,
    check_in: "2025-01-25T15:00:00.000Z",
    check_out: "2025-01-28T12:00:00.000Z",
    guest_count: 1,
    special_requests: "High-speed internet",
    payment_summary: {
      total_amount: 1800,
      deposit_paid: 540,
      remaining_amount: 1260,
      payment_method: "mastercard" as const
    },
    coupon_code: "BUSINESS10",
    created_at: "2025-01-22T11:00:00.000Z",
    updated_at: "2025-01-22T11:00:00.000Z"
  },
  {
    id: 506,
    customer: {
      id: 106,
      name: "Lisa Chen",
      email: "lisa@example.com",
      phone: "+966501234572",
      language: "en" as const,
      past_bookings: 1,
      average_rating: 4.6
    },
    property_id: 16,
    property_name: "Studio Apartment - Jeddah",
    status: "canceled" as const,
    check_in: "2025-01-18T15:00:00.000Z",
    check_out: "2025-01-20T12:00:00.000Z",
    guest_count: 1,
    special_requests: "City view",
    payment_summary: {
      total_amount: 600,
      deposit_paid: 180,
      remaining_amount: 420,
      payment_method: "cash" as const
    },
    cancellation_reason: "Change of plans",
    refund_amount: 150,
    created_at: "2025-01-15T14:00:00.000Z",
    updated_at: "2025-01-17T10:00:00.000Z"
  }
];

// Coupons data
export const coupons: Coupon[] = [
  {
    id: "1",
    code: "SAVE20",
    name: "20% Off Welcome Discount",
    description: "Get 20% off your first booking",
    discountType: "percentage",
    discountValue: 20,
    minOrderAmount: 100,
    maxDiscountAmount: 200,
    usageLimit: 1000,
    usagePerUser: 1,
    validFrom: "2024-01-01T00:00:00.000Z",
    validUntil: "2024-12-31T23:59:59.000Z",
    isActive: true,
    applicableCities: ["Riyadh", "Jeddah", "Dammam"],
    applicableVehicleTypes: ["sedan", "suv"],
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
    createdBy: "admin@example.com",
    totalUsage: 245,
    remainingUsage: 755
  },
  {
    id: "2",
    code: "EID50",
    name: "Eid Special - 50 SAR Off",
    description: "Special Eid discount of 50 SAR",
    discountType: "fixed",
    discountValue: 50,
    minOrderAmount: 200,
    usageLimit: 500,
    usagePerUser: 2,
    validFrom: "2024-06-15T00:00:00.000Z",
    validUntil: "2024-06-20T23:59:59.000Z",
    isActive: true,
    applicableCities: ["Riyadh", "Jeddah", "Dammam", "Mecca", "Medina"],
    createdAt: "2024-06-01T00:00:00.000Z",
    updatedAt: "2024-06-01T00:00:00.000Z",
    createdBy: "admin@example.com",
    totalUsage: 89,
    remainingUsage: 411
  },
  {
    id: "3",
    code: "WELCOME10",
    name: "New User 10% Off",
    description: "Welcome discount for new users",
    discountType: "percentage",
    discountValue: 10,
    minOrderAmount: 50,
    maxDiscountAmount: 100,
    usageLimit: 2000,
    usagePerUser: 1,
    validFrom: "2024-01-01T00:00:00.000Z",
    validUntil: "2024-12-31T23:59:59.000Z",
    isActive: true,
    applicableCities: ["Riyadh", "Jeddah", "Dammam"],
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
    createdBy: "admin@example.com",
    totalUsage: 1567,
    remainingUsage: 433
  },
  {
    id: "4",
    code: "SUMMER25",
    name: "Summer Special 25% Off",
    description: "Summer vacation special discount",
    discountType: "percentage",
    discountValue: 25,
    minOrderAmount: 300,
    maxDiscountAmount: 500,
    usageLimit: 300,
    usagePerUser: 1,
    validFrom: "2024-07-01T00:00:00.000Z",
    validUntil: "2024-08-31T23:59:59.000Z",
    isActive: false,
    applicableCities: ["Jeddah", "Dammam"],
    applicableVehicleTypes: ["suv", "van"],
    createdAt: "2024-06-15T00:00:00.000Z",
    updatedAt: "2024-08-31T23:59:59.000Z",
    createdBy: "admin@example.com",
    totalUsage: 298,
    remainingUsage: 2
  },
  {
    id: "5",
    code: "VIP100",
    name: "VIP Customer 100 SAR Off",
    description: "Exclusive discount for VIP customers",
    discountType: "fixed",
    discountValue: 100,
    minOrderAmount: 500,
    usageLimit: 100,
    usagePerUser: 3,
    validFrom: "2024-01-01T00:00:00.000Z",
    validUntil: "2024-12-31T23:59:59.000Z",
    isActive: true,
    applicableCities: ["Riyadh", "Jeddah", "Dammam", "Mecca", "Medina"],
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
    createdBy: "admin@example.com",
    totalUsage: 67,
    remainingUsage: 33
  }
];

// Coupon usage data
export const couponUsage: CouponUsage[] = [
  {
    id: "1",
    couponId: "1",
    userId: "101",
    bookingId: "501",
    discountAmount: 40,
    usedAt: "2024-01-15T10:30:00.000Z",
    userEmail: "ahmed@example.com",
    bookingAmount: 200
  },
  {
    id: "2",
    couponId: "1",
    userId: "102",
    bookingId: "502",
    discountAmount: 60,
    usedAt: "2024-01-16T14:20:00.000Z",
    userEmail: "sara@example.com",
    bookingAmount: 300
  },
  {
    id: "3",
    couponId: "2",
    userId: "103",
    bookingId: "503",
    discountAmount: 50,
    usedAt: "2024-06-16T09:15:00.000Z",
    userEmail: "omar@example.com",
    bookingAmount: 250
  },
  {
    id: "4",
    couponId: "3",
    userId: "104",
    bookingId: "504",
    discountAmount: 15,
    usedAt: "2024-01-20T16:45:00.000Z",
    userEmail: "fatima@example.com",
    bookingAmount: 150
  },
  {
    id: "5",
    couponId: "5",
    userId: "105",
    bookingId: "505",
    discountAmount: 100,
    usedAt: "2024-02-01T11:30:00.000Z",
    userEmail: "khalid@example.com",
    bookingAmount: 600
  }
];

// Coupon statistics
export const couponStats: CouponStats = {
  totalCoupons: 5,
  activeCoupons: 4,
  expiredCoupons: 1,
  totalUsage: 2066,
  totalDiscountGiven: 125847.50,
  averageDiscount: 60.92,
  topPerformingCoupons: [
    {
      coupon: coupons[2], // WELCOME10
      usage: 1567,
      revenue: 15670
    },
    {
      coupon: coupons[0], // SAVE20
      usage: 245,
      revenue: 12250
    },
    {
      coupon: coupons[1], // EID50
      usage: 89,
      revenue: 4450
    }
  ]
};

// Coupon analytics
export const couponAnalytics: CouponAnalytics = {
  usageByDate: [
    { date: "2024-01-01", usage: 45, discount: 2250 },
    { date: "2024-01-02", usage: 52, discount: 2600 },
    { date: "2024-01-03", usage: 38, discount: 1900 },
    { date: "2024-01-04", usage: 61, discount: 3050 },
    { date: "2024-01-05", usage: 47, discount: 2350 },
    { date: "2024-01-06", usage: 55, discount: 2750 },
    { date: "2024-01-07", usage: 43, discount: 2150 }
  ],
  usageByCoupon: [
    { couponId: "3", couponCode: "WELCOME10", usage: 1567, discount: 15670 },
    { couponId: "1", couponCode: "SAVE20", usage: 245, discount: 12250 },
    { couponId: "2", couponCode: "EID50", usage: 89, discount: 4450 },
    { couponId: "5", couponCode: "VIP100", usage: 67, discount: 6700 },
    { couponId: "4", couponCode: "SUMMER25", usage: 298, discount: 14900 }
  ],
  usageByCity: [
    { city: "Riyadh", usage: 856, discount: 42800 },
    { city: "Jeddah", usage: 743, discount: 37150 },
    { city: "Dammam", usage: 467, discount: 23350 },
    { city: "Mecca", usage: 234, discount: 11700 },
    { city: "Medina", usage: 156, discount: 7800 }
  ],
  conversionRate: 12.5,
  averageOrderValue: 285.50,
  averageOrderValueWithCoupon: 320.75
};

// Export all mock data
export const mockData = {
  currentUser,
  dashboardStats,
  drivers,
  riders,
  trips,
  vehicles,
  complaints,
  cities,
  vehicleCategories,
  finance,
  notifications,
  settings,
  bookings,
  coupons,
  couponUsage,
  couponStats,
  couponAnalytics,
}; 